import { Settings } from "lucide-react";
import {
  getAllSubcategories,
  getSearchableText,
} from "@/react/components/entityViews/settings/utils/settingsSearchUtils";
import {
  ENTITY_FINDER_ROW_HEIGHT_NO_SUBTITLE,
} from "@/modern/components/entity-finder/constants/layout";
import { navigateToSetting } from "@/modern/components/entity-finder/utils";
import type {
  EntityFinderConfigEntry,
  EntityFinderItem,
  EntityFinderListEntry,
  SettingItemRecord,
} from "@/modern/components/entity-finder/types";
import {
  defaultMatchesScope,
  matchesEntityFinderSearchQuery,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

function groupSettingsByCategory(items: EntityFinderItem<"settings">[]): EntityFinderItem<"settings">[][] {
  const map = new Map<string, EntityFinderItem<"settings">[]>();

  for (const item of items) {
    const record = item.record as SettingItemRecord;
    const key = record.category.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }

  return Array.from(map.values());
}

export const settingsConfig: EntityFinderConfigEntry<"settings"> = {
  type: "settings",
  label: "Setting",
  plural: "Settings",
  icon: Settings,
  tint: "bg-chart-4/15 text-chart-4",
  listOrder: 100,
  listRowVariant: "setting",
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "settings"),
  getData: (ctx) => {
    const trimmed = ctx.debouncedQuery.trim();
    if (!trimmed || !ctx.settingsReady) return [];

    return getAllSubcategories(ctx.settingsCategories)
      .filter((item) =>
        matchesEntityFinderSearchQuery(getSearchableText(item.subcategory, item.category), trimmed)
      )
      .map((item) => {
        const record: SettingItemRecord = {
          kind: "setting",
          subcategory: item.subcategory,
          category: item.category,
        };
        return {
          id: `setting-${item.subcategory.id}`,
          type: "settings" as const,
          label: item.subcategory.label,
          subtitle: item.subcategory.description,
          record,
        };
      });
  },
  buildListSection: (items, _ctx) => {
    const entries: EntityFinderListEntry[] = [];

    for (const categoryItems of groupSettingsByCategory(items)) {
      const categoryLabel = (categoryItems[0].record as SettingItemRecord).category.label;
      entries.push({ type: "header", label: categoryLabel, configType: "settings" });
      for (const item of categoryItems) {
        entries.push({ type: "setting", item, configType: "settings" });
      }
    }

    return entries;
  },
  estimateRowHeight: () => ENTITY_FINDER_ROW_HEIGHT_NO_SUBTITLE,
  itemRenderer: () => null,
  onSelect: (item, ctx) => {
    const record = item.record as SettingItemRecord;
    navigateToSetting(ctx.viewManager, record.subcategory);
  },
};
